import React from 'react';
import { useTheme } from '../contexts/ThemeContext';

const Logo = ({ className = '', animated = true }) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  return (
    <>
      <svg
        className={className}
        viewBox="0 0 200 40"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-label="Arbitrage Yield Logo"
      >
        <defs>
          <linearGradient id="logoGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={isDark ? "#4DC8A7" : "#0A7E66"} />
            <stop offset="100%" stopColor={isDark ? "#1AB98E" : "#086552"} />
          </linearGradient>
          <linearGradient id="accentGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#F5F5F5" />
            <stop offset="100%" stopColor="#C0C0C0" />
          </linearGradient>
          <style>
            {`
              .logo-circle {
                fill: none;
                stroke: url(#logoGradient);
                stroke-width: 1.5;
                ${animated ? `
                  stroke-dasharray: 100.5;
                  stroke-dashoffset: 100.5;
                  animation: drawCircle 2s ease-in-out forwards, pulseCircle 3s ease-in-out 2.5s infinite;
                ` : ''}
              }
              .logo-path {
                fill: none;
                stroke: url(#logoGradient);
                stroke-width: 2;
                stroke-linecap: round;
                stroke-linejoin: round;
                ${animated ? `
                  stroke-dasharray: 80;
                  stroke-dashoffset: 80;
                  animation: drawPath 1.8s ease-in-out 0.3s forwards, pulsePath 2.5s ease-in-out 2.3s infinite;
                ` : ''}
              }
              .logo-accent {
                fill: url(#accentGradient);
                ${animated ? `
                  opacity: 0;
                  animation: fadeIn 1.5s ease-out 0.8s forwards, pulseAccent 2s ease-in-out 2s infinite;
                ` : ''}
              }
              .logo-arrow {
                fill: url(#accentGradient);
                ${animated ? `
                  opacity: 0;
                  animation: fadeIn 1.2s ease-out 1s forwards, floatArrow 3s ease-in-out 2.2s infinite;
                ` : ''}
              }
              .logo-text-main {
                fill: url(#logoGradient);
                font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
                font-size: 16px;
                font-weight: 700;
                letter-spacing: -0.02em;
                ${animated ? `
                  opacity: 0;
                  transform: translateX(-10px);
                  animation: slideInFade 1.2s ease-out 1s forwards;
                ` : ''}
              }
              .logo-text-sub {
                fill: url(#accentGradient);
                font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
                font-size: 8px;
                font-weight: 400;
                letter-spacing: 0.12em;
                text-transform: uppercase;
                ${animated ? `
                  opacity: 0;
                  animation: fadeIn 1s ease-out 1.5s forwards;
                ` : ''}
              }
              @keyframes drawCircle {
                to { stroke-dashoffset: 0; }
              }
              @keyframes drawPath {
                to { stroke-dashoffset: 0; }
              }
              @keyframes fadeIn {
                to { opacity: 1; }
              }
              @keyframes slideInFade {
                to {
                  opacity: 1;
                  transform: translateX(0);
                }
              }
              @keyframes pulseCircle {
                0%, 100% { 
                  stroke-dashoffset: 0;
                  opacity: 1;
                  stroke-width: 1.5;
                }
                50% { 
                  stroke-dashoffset: -2;
                  opacity: 0.9;
                  stroke-width: 1.6;
                }
              }
              @keyframes pulsePath {
                0%, 100% { 
                  stroke-dashoffset: 0;
                  opacity: 1;
                  stroke-width: 2;
                }
                50% { 
                  stroke-dashoffset: -1;
                  opacity: 0.92;
                  stroke-width: 2.1;
                }
              }
              @keyframes pulseAccent {
                0%, 100% { 
                  opacity: 1;
                  transform: scale(1);
                }
                50% { 
                  opacity: 0.95;
                  transform: scale(1.02);
                }
              }
              @keyframes floatArrow {
                0%, 100% { 
                  opacity: 1;
                  transform: translateY(0) translateX(0) rotate(0deg);
                }
                33% { 
                  transform: translateY(-1.5px) translateX(0.5px) rotate(2deg);
                }
                66% { 
                  transform: translateY(1px) translateX(-0.5px) rotate(-1deg);
                }
              }
              @media (prefers-reduced-motion: reduce) {
                .logo-circle,
                .logo-path,
                .logo-accent,
                .logo-arrow,
                .logo-text-main,
                .logo-text-sub {
                  animation: none !important;
                  opacity: 1 !important;
                  stroke-dashoffset: 0 !important;
                  transform: translateX(0) translateY(0) rotate(0deg) scale(1) !important;
                }
              }
            `}
          </style>
        </defs>

        {/* Main Logo Mark - "AY" Monogram with Arbitrage-inspired design */}
        <g>
          {/* Outer circle - subtle stroke */}
          <circle
            cx="20"
            cy="20"
            r="16"
            className="logo-circle"
          />

          {/* "A" letterform - elegant and modern */}
          <path
            d="M 12 28 L 16 18 L 20 28 M 13.5 24 L 18.5 24"
            className="logo-path"
            strokeWidth="2.2"
          />

          {/* "Y" letterform - integrated with A */}
          <path
            d="M 22 18 L 24 22 L 24 28 M 24 22 L 28 18"
            className="logo-path"
            strokeWidth="2.2"
          />

          {/* Accent arrow - represents arbitrage flow/yield */}
          <path
            d="M 8 12 L 12 8 L 8 8 Z"
            className="logo-arrow"
          />
          <path
            d="M 32 12 L 28 8 L 32 8 Z"
            className="logo-arrow"
          />
        </g>

        {/* Wordmark */}
        <g>
          <text
            x="45"
            y="18"
            className="logo-text-main"
          >
            Arbitrage Yield
          </text>
          <text
            x="45"
            y="30"
            className="logo-text-sub"
          >
            Investment portal
          </text>
        </g>
      </svg>
    </>
  );
};

export default Logo;
